import { BasePaginationXsState } from "../../../base/base-pagination-xs.state";
import { JobTemplateModel } from "../../../../models/job_template/job-template.model";
import { BasePaginationStateModel } from "../../../base/model/base-pagination-state.model";
import { PaginationInterface } from "../../../interfaces/model/pagination.interface";
import { SortColumnInterface } from "../../../interfaces/model/sort-column.interface";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { DynamicFilterSortModel } from "../../../../models/dynamic-filter/dynamic-filter-sort.model";
import { SortColumnHeader } from "../../../../content/components/sort-column-header/sort-column-header";
import { Injectable } from "@angular/core";
import { DynamicFilterEntryDto } from "../../../../models/dynamic-filter/dynamic-filter-entry-dto.model";
import { RequestAction } from "../../../common/request.actions";
import { DynamicFilterRequestDto } from "../../../../models/dynamic-filter/dynamic-filter-request-dto.model";
import { CompoApp } from "../../../../services/CompoApp";
import { ObjectActionXsState } from "../object-action-xs.state";
import { HeaderClientService } from "../../../../services/client/header-service/header-client.service";
import { OaJobroutineForObjectsActions } from "./oa-jobroutine-for-objects.actions";
import { ObjectDetailModel } from "../../../../models/component/page/detail/object-detail.model";
import {
  JOBROUTINE_BULK
} from "../../../../content/components/sort-column-header/object-list-sort-column/sort-columns";
import { ObjectFilterUtil } from "../../../../utils/filter/object-filter.util";
import { DynamicFilterTemplatePage } from '../../../../models/dynamic-filter/dynamic-filter-template-page.model';
import { FilterTemplateEntryName } from '../../../../enums/dynamic-filter/filter-template-entry-name.enum';
import Status = RequestAction.Status;

export class OaJobroutineForObjectsStateModel extends BasePaginationStateModel<OaJobroutineForObjectsStateModel, ObjectDetailModel> implements PaginationInterface<JobTemplateModel>,
  SortColumnInterface {

  //region pagination
  pageSize: number;
  currentPage: number;
  totalPages: number;
  totalCount: number;

  values: any;

  fetchedPages: Set<number>;
  fetchTimestamp: number;

  filterForm: UntypedFormGroup;
  filterSettings: DynamicFilterTemplatePage;
  filterEntryMap: Map<string, string>;

  //endregion pagination

  //region sorting
  gridTemplateColumn: string;

  sorting: DynamicFilterSortModel[];
  sortColumns: SortColumnHeader[];

  selectedColumns: UntypedFormGroup;
  //endregion sorting

  //region request
  currentRequest: RequestAction.Status;
  //endregion request

  objectIds: Set<string>;
  objectSelectedIds: Set<string>;

  generateJobs: boolean;
  generateRoutines: boolean;

  objRoutineMap: Map<string, any>;
}

@State<OaJobroutineForObjectsStateModel>({
  name: 'OaJobroutineForObjects',
  defaults: {

    //region pagination
    pageSize: 25,
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,

    values: Array.from({length: 25}),
    fetchedPages: new Set(),
    fetchTimestamp: null,

    filterForm: new UntypedFormGroup({}),
    filterSettings: null,
    filterEntryMap: new Map<string, string>(),

    //endregion pagination

    //region sorting
    gridTemplateColumn: SortColumnHeader.concatGridTemplateColumns(JOBROUTINE_BULK),

    sorting: [],
    sortColumns: JOBROUTINE_BULK,

    selectedColumns: new UntypedFormGroup({selectedColumns: new UntypedFormControl([])}),
    //endregion sorting

    //region request
    currentRequest: RequestAction.Status.NONE,
    //endregion request

    //all possible objects from the selection before the dialog was opened.
    objectIds: new Set(),
    //object selection within the dialog
    objectSelectedIds: new Set(),

    generateJobs: false,
    generateRoutines: true,
    objRoutineMap: new Map<string, any>(),
  }
})
@Injectable()
export class OaJobroutineForObjectsXsState extends BasePaginationXsState<OaJobroutineForObjectsStateModel, JobTemplateModel> {

  constructor(
    protected app: CompoApp,
    private store: Store,
    private headerService: HeaderClientService
  ) {
    super(app);
  }

  //region SELECTORS
  //region pagination

  @Selector()
  static filterForm(state: OaJobroutineForObjectsStateModel): UntypedFormGroup {
    return state.filterForm;
  }

  @Selector()
  static filterSearchTermControlName(state: OaJobroutineForObjectsStateModel): string {
    const id = state.filterEntryMap.get(ObjectFilterUtil.SEARCH_TERM);

    if (id) return id.toString();

    return "";
  }

  @Selector()
  static filterSettings(state: OaJobroutineForObjectsStateModel): DynamicFilterTemplatePage {
    return state.filterSettings;
  }

  @Selector()
  static filterSettingsEntries(state: OaJobroutineForObjectsStateModel): Record<FilterTemplateEntryName, DynamicFilterEntryDto> {
    return state.filterSettings.filterEntries;
  }

  @Selector()
  static values(state: OaJobroutineForObjectsStateModel): JobTemplateModel[] {
    return state.values;
  }

  @Selector()
  static selectedObjectIds(state: OaJobroutineForObjectsStateModel): Set<string> {
    return new Set(state.objectSelectedIds);
  }

  @Selector()
  static selectedObjectIdsArray(state: OaJobroutineForObjectsStateModel): string[] {
    return [...(state.objectSelectedIds || [])];
  }

  //endregion pagination

  //region sorting
  @Selector()
  static sorting(state: OaJobroutineForObjectsStateModel): DynamicFilterSortModel[] {
    return state.sorting;
  }

  @Selector()
  static sortColumns(state: OaJobroutineForObjectsStateModel): SortColumnHeader[] {
    return state.sortColumns;
  }

  @Selector()
  static visibleSortColumns(state: OaJobroutineForObjectsStateModel): SortColumnHeader[] {
    return state.sortColumns.filter(column => column.visible);
  }

  @Selector()
  static selectedColumns(state: OaJobroutineForObjectsStateModel): UntypedFormGroup {
    return state.selectedColumns;
  }

  @Selector()
  static gridTemplateColumns(state: OaJobroutineForObjectsStateModel): string {
    return state.gridTemplateColumn;
  }

  //endregion sorting

  @Selector()
  static generateJobs(state: OaJobroutineForObjectsStateModel): boolean {
    return state.generateJobs;
  }

  @Selector()
  static objRoutineMap(state: OaJobroutineForObjectsStateModel): Map<string, any> {
    return state.objRoutineMap;
  }

  @Selector()
  static generateRoutines(state: OaJobroutineForObjectsStateModel): boolean {
    return state.generateRoutines;
  }

  //endregion SELECTORS

  //region ACTIONS
  //region pagination
  @Action(OaJobroutineForObjectsActions.ResetPagination)
  public resetPagination(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.ResetPagination) {
    const state = ctx.getState();

    ctx.patchState({
      currentRequest: Status.NONE,
      values: Array.from({length: state.pageSize}),
      fetchedPages: new Set()
    });
  }

  @Action(OaJobroutineForObjectsActions.FetchPageSettings)
  public fetchPageSettings(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.FetchPageSettings) {
    super.paginationFetchPageSettings(ctx, action.pageEnum);
  }

  protected paginationPatchStateOnSettingsCallback(ctx: StateContext<OaJobroutineForObjectsStateModel>, filterSettings) {
    const state = ctx.getState(), filterEntryMap = new Map<string, string>();

    if (filterSettings) {
      const filterEntry = filterSettings.filterEntries[ObjectFilterUtil.SEARCH_TERM];
      if (filterEntry) {
        filterEntryMap.set(ObjectFilterUtil.SEARCH_TERM, filterEntry.id);
      }
    }

    ctx.patchState({
      filterEntryMap: filterEntryMap,
      filterSettings: filterSettings
    });
  }

  @Action(OaJobroutineForObjectsActions.FetchPage)
  public fetchPage(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.FetchPage) {
    const state = ctx.getState(), page = action.page, dto = action.dto,
      fetchedPages = state.fetchedPages, pageSize = state.pageSize,
      values = state.values;

    const fetchTimestamp = super.paginationFetchPage(page, fetchedPages, dto);

    //page should not be fetched -> virtual scrollable container is not big enough.
    if (fetchTimestamp === -1) return;

    //page is already fetched
    if (fetchTimestamp === -2) return;

    //continue
    ctx.patchState({
      fetchTimestamp: fetchTimestamp
    });

    super.paginationDoRequest(ctx, page, pageSize, dto, fetchedPages, values, fetchTimestamp);
  }

  @Action(OaJobroutineForObjectsActions.FetchPages)
  public fetchPages(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.FetchPages) {
    const state = ctx.getState(), fetchedPages = state.fetchedPages, pageSize = state.pageSize,
      values = state.values, currentRequest = state.currentRequest;
    let dto = action.dto;

    if (!state.filterSettings) return;

    //a request is currently executed
    if (currentRequest === RequestAction.Status.PENDING) return;

    if (!dto) {
      dto = this.paginationGetFilterDto(ctx, action.filterValues);
    }

    if (action.selectedIds && action.selectedIds.length > 0) {
      dto.selectedIds = action.selectedIds;
    }

    //There might not be an implementation yet!
    if (!dto) return;

    const pages = super.paginationOnEvent(action.event, pageSize);

    //No pages to fetch
    if (pages.length < 1) return;

    const pageNumber = pages.filter(pN => !fetchedPages.has(pN))[0] || pages[0];
    const fetchTimestamp = super.paginationFetchPage(pageNumber, fetchedPages, dto);

    //page should not be fetched -> virtual scrollable container is not big enough.
    if (fetchTimestamp === -1) return;

    //page is already fetched
    if (fetchTimestamp === -2) return;

    //continue
    ctx.patchState({
      fetchTimestamp: fetchTimestamp,
      currentRequest: RequestAction.Status.PENDING
    });

    pages
      .filter(pN => !fetchedPages.has(pN))
      .forEach(page => {
        super.paginationDoRequest(ctx, page, pageSize, dto, fetchedPages, values, fetchTimestamp);
      });
  }

  protected paginationCreateFilterDto(): DynamicFilterRequestDto {
    return undefined;
  }

  protected paginationGetFilterDto(ctx: StateContext<OaJobroutineForObjectsStateModel>, filterValues: any): DynamicFilterRequestDto {
    const request = new DynamicFilterRequestDto(), state = ctx.getState(), filterSettings = state.filterSettings;
    request.templateId = filterSettings?.id;

    request.filterValues = DynamicFilterRequestDto.initFilterValues(filterSettings.filterEntries, filterValues);
    request.filterRangeValues = DynamicFilterRequestDto.initFilterRangeValues(filterSettings.filterEntries, filterValues);
    request.filterAttributeValues = DynamicFilterRequestDto.initFilterAttributeValues(filterSettings.filterEntries, filterValues);

    request.orgIds = this.headerService.getAllOrgIds();
    request.locationIds = this.headerService.getAllLocationIds();

    request.selectedIds = [...ctx.getState().objectIds];

    request.sortDtos = state.sorting;

    return request;
  }

  protected paginationPatchStateOnCallback(
    ctx: StateContext<OaJobroutineForObjectsStateModel>,
    values: JobTemplateModel[],
    fetchedPages: Set<number>,
    totalCount: number,
    totalPages: number
  ): void {
    ctx.patchState({
      currentRequest: RequestAction.Status.SUCCESS,
      values: values,
      fetchedPages: fetchedPages,
      totalCount: totalCount,
      totalPages: totalPages
    });
  }

  protected paginationPatchStateOnError(
    ctx: StateContext<OaJobroutineForObjectsStateModel>,
    fetchedPages: Set<number>
  ): void {
    ctx.patchState({
      currentRequest: RequestAction.Status.ERROR,
      fetchedPages: fetchedPages
    });
  }

  //endregion pagination

  //region sorting
  @Action(OaJobroutineForObjectsActions.SetSortColumnHeaders)
  public setSortColumnHeaders(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.SetSortColumnHeaders) {
    ctx.patchState({
      sortColumns: action.columns,
      gridTemplateColumn: super.sortingGridTemplateColumn(ctx.getState().sortColumns)
    });
  }

  @Action(OaJobroutineForObjectsActions.OnColumnHeaderClick)
  public onColumnHeaderClick(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.OnColumnHeaderClick) {
    //region action impl.
    const state = ctx.getState();

    if (!this.store.selectSnapshot(ObjectActionXsState.jobroutineActionDialogVisible)) return;

    //columnField is required to execute common logic!
    if (!action.columnField || action.columnField.length <= 0) return;

    const columnIdx = state.sortColumns.findIndex(c => c.columnField === action.columnField);
    if (columnIdx === -1) return;

    if (!state.sortColumns[columnIdx].visible) return;

    if (state.sortColumns[columnIdx].disabledSort) return;

    const newSorting = super.sortingOnColumnHeaderClick([...state.sorting], action.columnField);
    const newSortColumns = super.sortingOnColumnHeaderClickSortColumns([...state.sortColumns], action.columnField, newSorting);
    ctx.patchState({
      sortColumns: newSortColumns,
      sorting: newSorting,
      gridTemplateColumn: super.sortingGridTemplateColumn(state.sortColumns)
    });
    //endregion action impl.
  }

  //endregion sorting

  @Action(OaJobroutineForObjectsActions.Clear)
  public clear(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.Clear) {
    ctx.dispatch(new OaJobroutineForObjectsActions.ResetPagination());
    ctx.patchState({
      objectSelectedIds: new Set<string>(),
      generateJobs: false,
      generateRoutines: true
    });
  }

  @Action(OaJobroutineForObjectsActions.SetObjectIds)
  public setObjectIds(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.SetObjectIds) {
    ctx.patchState({
      objectIds: new Set<string>(action.objectIds)
    });
  }

  @Action(OaJobroutineForObjectsActions.SetObjRoutineMap)
  public setObjRoutineMap(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.SetObjRoutineMap) {
    ctx.patchState({
      objRoutineMap: new Map(action.map)
    });
  }

  @Action(OaJobroutineForObjectsActions.SetObjectSelectedIds)
  public setSelectedObjectIds(ctx: StateContext<OaJobroutineForObjectsStateModel>, action: OaJobroutineForObjectsActions.SetObjectSelectedIds) {
    ctx.patchState({
      objectSelectedIds: new Set<string>(action.objectIds)
    });
  }

  @Action(OaJobroutineForObjectsActions.SetGenerateJobs)
  public setGenerateJobs(
    ctx: StateContext<OaJobroutineForObjectsStateModel>,
    action: OaJobroutineForObjectsActions.SetGenerateJobs
  ) {
    ctx.patchState({
      generateJobs: action.generateJobs
    });
  }

  @Action(OaJobroutineForObjectsActions.SetGenerateRoutines)
  public setGenerateRoutines(
    ctx: StateContext<OaJobroutineForObjectsStateModel>,
    action: OaJobroutineForObjectsActions.SetGenerateRoutines
  ) {
    ctx.patchState({
      generateRoutines: action.generateRoutines
    });
  }

  //endregion ACTIONS
}
